import { useState, useRef, useLayoutEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Sparkle, TrendingUp } from "lucide-react";

/* Hero — Figma node 102:2925 (Frame 97), 1426 × 907.

   Desktop (lg+) places every element at its real Figma coordinate inside a
   1426×907 stage that is then uniformly scaled to the panel width. That keeps
   the composition pixel-faithful at any viewport instead of drifting the way
   independent percentage positioning does. Below lg the stage is dropped for a
   stacked layout. */

const STAGE_W = 1426;
const STAGE_H = 907;

function ArrowCircle({ className = "" }) {
  return (
    <span className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-white ${className}`}>
      <ArrowUpRight size={16} className="text-black" />
    </span>
  );
}

/* Figma uses a flat rgba(0,0,0,0.2) scrim; lightened here so the photography
   reads more clearly, with a bottom gradient to hold caption contrast. */
function CardMedia({ src }) {
  return (
    <>
      <img src={src} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
      <span className="absolute inset-0 bg-black/40" aria-hidden />
      <span className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/45 to-transparent" aria-hidden />
    </>
  );
}

const cardBase =
  "group relative block shrink-0 overflow-hidden snap-start transition-transform duration-300 hover:-translate-y-1";

/* node 102:2942 */
function CardContentCreation({ className = "" }) {
  return (
    <Link to="/courses" className={`${cardBase} h-[382px] w-[232px] ${className}`}>
      <CardMedia src="/images/hero-card-content-creation.jpg" />
      <span className="absolute left-[27px] top-[30px] flex h-[46px] w-[92px] items-center justify-center rounded-[16px] bg-[var(--lime)] font-poppins text-[24px] font-medium tracking-[1.44px] text-black">
        LIVE
      </span>
      <span className="absolute left-[29px] top-[180px] font-poppins text-[14px] font-medium text-white">
        Content Creation
      </span>
      <span className="absolute left-[27px] top-[212px] block w-[169px] font-montserrat text-[32px] font-semibold leading-[33px] text-white">
        From Ideas to Impact
      </span>
      <span className="absolute left-[27px] top-[327px] flex items-center gap-[12px]">
        <span className="font-poppins text-[16px] text-white">By Ankit Singh</span>
        <ArrowCircle className="transition-transform group-hover:scale-110" />
      </span>
    </Link>
  );
}

/* node 102:2952 */
function CardUiUx({ className = "" }) {
  return (
    <Link to="/courses" className={`${cardBase} h-[454px] w-[292px] rounded-[24px] ${className}`}>
      <CardMedia src="/images/hero-card-uiux.jpg" />
      <span className="absolute left-[40px] top-[36px] flex h-[46px] w-[212px] items-center justify-center rounded-[20px] border border-white font-poppins text-[16px] font-medium text-white">
        Designing for Brands
      </span>
      <span className="absolute left-[40px] top-[264px] flex w-[190px] flex-col gap-[20px]">
        <span className="font-montserrat text-[36px] font-semibold leading-[36px] text-white">
          UI/UX Basics to Advanced
        </span>
        <span className="flex items-center gap-[12px]">
          <span className="whitespace-nowrap font-poppins text-[16px] text-white">By Shruti Verma</span>
          <ArrowCircle className="transition-transform group-hover:scale-110" />
        </span>
      </span>
    </Link>
  );
}

/* node 102:2962 */
function CardCourses({ className = "" }) {
  return (
    <Link to="/courses" className={`${cardBase} h-[382px] w-[232px] ${className}`}>
      <CardMedia src="/images/hero-card-courses.jpg" />
      <span className="absolute left-[31px] top-[39px] flex items-center gap-[12px]">
        <span className="font-poppins text-[24px] font-medium text-white">Explore</span>
        <ArrowCircle className="transition-transform group-hover:scale-110" />
      </span>
      <span className="absolute left-[31px] top-[217px] block w-[190px] font-montserrat text-[36px] font-semibold leading-[36px] text-white">
        250+ Courses
      </span>
      <span className="absolute left-[31px] top-[299px] block w-[171px] font-poppins text-[16px] leading-[22px] text-white">
        Industry-led courses for every skill level.
      </span>
    </Link>
  );
}

/* Mobile rail. The cards are three different fixed sizes because their internals
   are absolutely positioned, so each is scaled to one shared 292×454 slot — `fit`
   is that normalising factor. Without it the focused card would not be the
   largest one whenever a small card is centred.

   The slot size never changes; only the inner transform does. Keeping the layout
   box fixed means the snap points stay put while the scale animates. */
const SLOT_W = 292;
const SLOT_H = 454;
const FOCUS_SCALE = 1;
const REST_SCALE = 0.86;

const MOBILE_CARDS = [
  { key: "uiux", Card: CardUiUx, fit: 1, delay: "0.10s" },
  { key: "content", Card: CardContentCreation, fit: SLOT_H / 382, delay: "0.16s" },
  { key: "courses", Card: CardCourses, fit: SLOT_H / 382, delay: "0.22s" },
];

export default function HeroSection() {
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const railRef = useRef(null);
  const [scale, setScale] = useState(0);
  const [active, setActive] = useState(0);

  const slidesOf = (el) => Array.from(el.querySelectorAll("[data-slide]"));

  // The dot follows whichever card is nearest the rail's centre, so it tracks a
  // real swipe rather than only a tap on the dots.
  const onRailScroll = () => {
    const el = railRef.current;
    if (!el) return;
    const centre = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    slidesOf(el).forEach((s, i) => {
      const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - centre);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setActive(best);
  };

  const goToCard = (i) => {
    const el = railRef.current;
    const slide = el && slidesOf(el)[i];
    if (!slide) return;
    el.scrollTo({ left: slide.offsetLeft + slide.offsetWidth / 2 - el.clientWidth / 2, behavior: "smooth" });
  };

  // Measure synchronously before paint, then track resizes, so the scaled stage
  // never flashes at the wrong size.
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / STAGE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const goPackage = () => navigate("/package");
  const goPartner = () => navigate("/partner");
  const scrollOn = () => window.scrollTo({ top: window.innerHeight, behavior: "smooth" });

  return (
    // Pulled up so the panel sits behind the transparent sticky navbar, as in Figma.
    <section className="relative -mt-[88px] px-3 pb-8 sm:px-4 lg:px-4 xl:pb-10">
      <div className="relative mx-auto max-w-[1920px] overflow-hidden rounded-[24px] bg-[var(--fig-surface)]">

        {/* ── desktop stage ── */}
        <div
          ref={stageRef}
          className="relative hidden w-full lg:block"
          style={{ height: scale ? STAGE_H * scale : undefined }}
        >
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{
              width: STAGE_W,
              height: STAGE_H,
              transform: `scale(${scale || 1})`,
              visibility: scale ? "visible" : "hidden",
            }}
          >
            {/* Nothing in the stage sets z-index, so DOM order is the stacking
                order: pastel blocks first, cards over them. */}
            <div className="anim-fade absolute rounded-[19.023px] bg-[var(--fig-periwinkle)]" style={{ left: 706.37, top: 185.84, width: 291.52, height: 265.64, animationDelay: "0.30s" }} aria-hidden />
            <div className="anim-fade absolute rounded-[19.023px] bg-[var(--fig-pink)]" style={{ left: 663.98, top: 496, width: 240.53, height: 266, animationDelay: "0.36s" }} aria-hidden />
            <div className="anim-fade absolute rounded-[19.023px] bg-[var(--fig-mint)]" style={{ left: 1086.43, top: 233, width: 248.62, height: 266, animationDelay: "0.42s" }} aria-hidden />

            {/* The entrance rise sits on these wrappers, not on the cards, so the
                cards keep their own hover:-translate-y-1. */}
            <div className="anim-rise absolute" style={{ left: 611, top: 302, animationDelay: "0.16s" }}>
              <CardContentCreation className="rounded-l-[24px]" />
            </div>
            <div className="anim-rise absolute" style={{ left: 855, top: 266, animationDelay: "0.10s" }}>
              <CardUiUx />
            </div>
            <div className="anim-rise absolute" style={{ left: 1159, top: 302, animationDelay: "0.22s" }}>
              <CardCourses className="rounded-r-[24px]" />
            </div>

            <div className="anim-pop absolute overflow-hidden rounded-full" style={{ left: 529, top: 167, width: 82, height: 82, animationDelay: "0.46s" }} aria-hidden>
              <img src="/images/hero-avatar-2.jpg" alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="anim-pop absolute overflow-hidden rounded-full" style={{ left: 1281, top: 152, width: 68, height: 68, animationDelay: "0.54s" }} aria-hidden>
              <img src="/images/hero-avatar-1.jpg" alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
            <div className="anim-pop absolute flex items-center justify-center rounded-full bg-[#FFD2E9]" style={{ left: 994, top: 127, width: 68, height: 68, animationDelay: "0.50s" }} aria-hidden>
              <TrendingUp size={35} strokeWidth={1.75} className="text-black" />
            </div>
            <div className="anim-pop absolute flex items-center justify-center rounded-full bg-[var(--fig-aqua)]" style={{ left: 483, top: 269, width: 50, height: 50, animationDelay: "0.58s" }} aria-hidden>
              <Sparkle size={26} fill="currentColor" className="text-black" />
            </div>
            <div className="anim-pop absolute rounded-full bg-[var(--fig-blue-dot)]" style={{ left: 559, top: 676, width: 22, height: 22, animationDelay: "0.62s" }} aria-hidden />
            <div className="anim-pop absolute rounded-full bg-[var(--lime)]" style={{ left: 1329, top: 737, width: 34, height: 34, animationDelay: "0.66s" }} aria-hidden />

            <div className="anim-rise absolute flex items-center justify-center rounded-[45px] bg-[var(--fig-lilac)] font-poppins text-[16px] text-black" style={{ left: 55, top: 201, width: 310, height: 47 }}>
              YOUR GROWTH, OUR ECOSYSTEM
            </div>

            {/* The marker carries a rotation, so it fades in rather than rising —
                .anim-rise would overwrite that transform. */}
            <div className="anim-fade absolute -rotate-[3.58deg] rounded-[29.557px] bg-[var(--lime)]" style={{ left: 52.25, top: 500, width: 271.884, height: 112.43, animationDelay: "0.34s" }} aria-hidden />
            <h1 className="anim-rise absolute font-poppins text-[96px] font-medium leading-[115px] text-black" style={{ left: 80.76, top: 265, animationDelay: "0.08s" }}>
              Learn.<br />Build.<br />RISE.
            </h1>

            <p className="anim-rise absolute font-poppins text-[20px] leading-[26px] text-[var(--fig-body)]" style={{ left: 81, top: 653, width: 380, animationDelay: "0.18s" }}>
              Learn from experts, build practical skills, and unlock new opportunities.
            </p>

            {/* Wrapped so the entrance transform does not clobber active:scale-95. */}
            <div className="anim-rise absolute" style={{ left: 55, top: 752, animationDelay: "0.26s" }}>
              <button
                onClick={goPackage}
                className="rounded-[45px] bg-[var(--lime)] font-poppins text-[18px] text-black transition-all hover:brightness-95 active:scale-95"
                style={{ width: 216, height: 56 }}
              >
                Explore Packages
              </button>
            </div>
            <div className="anim-rise absolute" style={{ left: 291, top: 752, animationDelay: "0.32s" }}>
              <button
                onClick={goPartner}
                className="rounded-[45px] border border-black font-poppins text-[18px] text-black transition-all hover:bg-black hover:text-white active:scale-95"
                style={{ width: 216, height: 56 }}
              >
                Become a Creator
              </button>
            </div>

            {/* Overruns the stage height; the panel's overflow-hidden clips it, as in Figma. */}
            <button
              onClick={scrollOn}
              className="anim-fade absolute left-1/2 flex -translate-x-1/2 items-center justify-center rounded-t-[45px] bg-white font-poppins text-[18px] text-black transition-colors hover:text-[var(--fig-body)]"
              style={{ top: 843, width: 281, height: 71, animationDelay: "0.7s" }}
            >
              SCROLL TO EXPLORE
            </button>
          </div>
        </div>

        {/* ── below lg: stacked ── */}
        <div className="relative px-5 pb-[92px] pt-[116px] sm:px-8 lg:hidden">

          {/* Tuned against the ~390px frame and painted first so they stay behind
              the copy. The panel's overflow-hidden clips the ones that bleed past
              an edge, which is the half-cut look the design uses. */}
          <div className="anim-fade pointer-events-none absolute -right-6 top-[86px] h-[56px] w-[56px] rounded-[19.023px] bg-[var(--lime)]" style={{ animationDelay: "0.34s" }} aria-hidden />
          <div className="anim-pop pointer-events-none absolute right-2 top-[344px] h-[22px] w-[22px] rounded-full bg-[var(--fig-blue-dot)]" style={{ animationDelay: "0.62s" }} aria-hidden />
          {/* Half-clipped at the left edge, vertically centred on the "RISE." line. */}
          <div className="anim-pop pointer-events-none absolute -left-6 top-[238px] z-10 flex h-[50px] w-[50px] items-center justify-center rounded-full bg-[var(--fig-aqua)]" style={{ animationDelay: "0.58s" }} aria-hidden>
            <Sparkle size={24} fill="currentColor" className="text-black" />
          </div>

          <div className="relative flex flex-col items-center text-center">
            <div className="anim-rise inline-flex h-[38px] items-center justify-center rounded-[45px] bg-[var(--fig-lilac)] px-6 font-poppins text-[12px] text-black min-[420px]:text-[13px] sm:h-[47px] sm:text-[16px]">
              YOUR GROWTH, OUR ECOSYSTEM
            </div>

            <h1 className="anim-rise mt-5 font-poppins text-[52px] font-medium leading-[1.14] text-black min-[420px]:text-[60px] sm:text-[76px]" style={{ animationDelay: "0.08s" }}>
              <span className="block">Learn. Build.</span>
              <span className="relative inline-block">
                <span className="absolute -inset-x-4 inset-y-0 -rotate-[3.58deg] rounded-[14px] bg-[var(--lime)]" aria-hidden />
                <span className="relative">RISE.</span>
              </span>
            </h1>

            <p className="anim-rise mt-6 max-w-[300px] font-poppins text-[15px] leading-[24px] text-[var(--fig-body)] min-[420px]:max-w-[330px] min-[420px]:text-[16px] sm:text-[18px] sm:leading-[28px]" style={{ animationDelay: "0.18s" }}>
              Learn from experts, build practical skills, and unlock new opportunities.
            </p>

            {/* flex-1 + nowrap keeps both labels on a single line, as in Figma. */}
            <div className="anim-rise mt-7 flex w-full flex-row gap-3 sm:w-auto sm:gap-5" style={{ animationDelay: "0.26s" }}>
              <button onClick={goPackage} className="h-[46px] flex-1 whitespace-nowrap rounded-[45px] bg-[var(--lime)] px-2 font-poppins text-[13px] text-black transition-all hover:brightness-95 active:scale-95 min-[420px]:text-[14px] sm:h-14 sm:w-[216px] sm:flex-none sm:px-8 sm:text-[18px]">
                Explore Packages
              </button>
              <button onClick={goPartner} className="h-[46px] flex-1 whitespace-nowrap rounded-[45px] border border-black px-2 font-poppins text-[13px] text-black transition-all hover:bg-black hover:text-white active:scale-95 min-[420px]:text-[14px] sm:h-14 sm:w-[216px] sm:flex-none sm:px-8 sm:text-[18px]">
                Become a Creator
              </button>
            </div>
          </div>

          {/* Decorative pops mirroring the desktop stage's avatar/trending stickers,
              spread across the band between the CTAs and the card rail. */}
          <div className="anim-rise relative mt-10 flex items-center justify-between" style={{ animationDelay: "0.34s" }}>
            <div className="anim-pop ml-8 flex h-[58px] w-[58px] items-center justify-center rounded-full bg-[#FFD2E9]" style={{ animationDelay: "0.5s" }} aria-hidden>
              <TrendingUp size={28} strokeWidth={1.75} className="text-black" />
            </div>
            <div className="anim-pop -mr-4 h-[76px] w-[76px] overflow-hidden rounded-full" style={{ animationDelay: "0.54s" }} aria-hidden>
              <img src="/images/hero-avatar-2.jpg" alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
          </div>

          <div className="relative mt-4">
            {/* Anchored to the rail rather than a page offset, so the blocks keep
                the same tuck behind the card whatever the copy above reflows to.
                First in DOM order, so the cards paint over them. */}
            <div className="anim-fade pointer-events-none absolute -left-4 -top-[10px] h-[118px] w-[198px] rounded-[19.023px] bg-[var(--fig-periwinkle)]" style={{ animationDelay: "0.30s" }} aria-hidden />
            <div className="anim-fade pointer-events-none absolute -right-6 top-[7px] h-[116px] w-[150px] rounded-[19.023px] bg-[var(--fig-mint)]" style={{ animationDelay: "0.42s" }} aria-hidden />
            <div className="anim-fade pointer-events-none absolute -left-8 top-[404px] h-[118px] w-[128px] rounded-[19.023px] bg-[var(--fig-pink)]" style={{ animationDelay: "0.36s" }} aria-hidden />

            {/* snap-center + half-card spacers centre the active card with its
                neighbours peeking either side. Entrance sits on the slot, not the
                card: anim-rise ends on `transform: none` and would clobber both
                the focus scale and the card's own hover lift. */}
            <div
              ref={railRef}
              onScroll={onRailScroll}
              className="hide-scrollbar -mx-5 flex snap-x snap-mandatory items-center gap-3 overflow-x-auto scroll-smooth pb-2 pt-8 sm:-mx-8"
            >
              <span className="shrink-0" style={{ width: `calc(50% - ${SLOT_W / 2}px)` }} aria-hidden />
              {MOBILE_CARDS.map(({ key, Card, fit, delay }, i) => (
                <div
                  key={key}
                  data-slide
                  className="anim-rise flex shrink-0 snap-center items-center justify-center"
                  style={{ width: SLOT_W, height: SLOT_H, animationDelay: delay }}
                >
                  <div
                    className="transition-transform duration-300 ease-out"
                    style={{ transform: `scale(${fit * (i === active ? FOCUS_SCALE : REST_SCALE)})` }}
                  >
                    <Card className="rounded-[24px]" />
                  </div>
                </div>
              ))}
              <span className="shrink-0" style={{ width: `calc(50% - ${SLOT_W / 2}px)` }} aria-hidden />
            </div>

            <div className="mt-12 flex items-center justify-center gap-2.5">
              {MOBILE_CARDS.map((c, i) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => goToCard(i)}
                  aria-label={`Show card ${i + 1}`}
                  aria-current={i === active}
                  className={`rounded-full transition-all ${i === active ? "h-[10px] w-[10px] bg-black" : "h-[8px] w-[8px] bg-black/20"}`}
                />
              ))}
            </div>
          </div>

          <button
            onClick={scrollOn}
            className="anim-fade absolute bottom-0 left-1/2 flex h-[58px] w-[240px] -translate-x-1/2 items-center justify-center rounded-t-[45px] bg-white font-poppins text-[15px] text-black transition-colors hover:text-[var(--fig-body)]"
            style={{ animationDelay: "0.7s" }}
          >
            SCROLL TO EXPLORE
          </button>
        </div>
      </div>
    </section>
  );
}
